import { AnyBoosterType, createAnyBoostersRecord } from 'State/Card/BoosterType';
import { AudioStreamType } from 'System/Audio/AudioSystem';
import { TimerType } from 'System/Timer/TimerTypes';
import { RewardType } from 'Utility/Rewards/RewardType';

// Dummies to define type in dummy objects
let string: string;
let number: number;
let boolean: boolean;
let nullableNumber: number | null;
let nullableString: string | null;
let boostersArray: AnyBoosterType[];

/** A dummmy JS object to define a type and have list of keys in runtime at the same time */
const iPlayerDataTypeObjectMandatory = {
    GBCUserId: string,

    /**
     * Store the most recent endpoint timestamp.
     * @example Fri May 10 2019 16:11:56 GMT+0200 (Central European Summer Time)
     */
    data_timestamp: string,

    /**
     * Data backup recorded the first time the user plays SOLI v2.
     * Recorded as redundancy for roll-back and/or data-loss scenarios.
     */
    data_backup: string,
    /** Game Version */
    version: string,

    v1_45_0_totalAdsWatched: number,

    // Events
    FBEvents: string,
    purchases: number,
    v2_28_3_productsPurchased: string,
    v1_39_0_realMoneySpent: number,
    v1_39_0_coinValueBoughtWithRealMoney: number,
    v1_71_0_lastSundayOfferPurchaseTimestamp: number,
    sessionCount: number,
    totalSessionCount: number,
    botOptIn: number,
    hasShortcut: number,
    v1_84_0_mAdName: string,
    gdprCompliance: boolean,
    dataConflict: boolean,
    currentEventOffer: string,
    __lostData: string,
} as const;

/** A dummmy JS object to define a type and have list of keys in runtime at the same time */
const iPlayerDataTypeObjectOptional = {
    // Inventory --------------------------------------------------------------
    ...createAnyBoostersRecord(() => number),
    [RewardType.FREE_PLAY]: number,
    [RewardType.PREMIUM_SPIN]: number,
    coins: number,
    keys: number,
    wheelSpins: number,
    freeChest: number,
    unlockedBoosters: string,
    [RewardType.PLINKO_TOKEN]: number,

    // Chests

    v1_44_0_starChestInitialStars: number,
    v1_44_0_starChestLevel: nullableNumber,
    v1_44_0_luckyTurnipsState: nullableString,

    // Pets
    v1_71_5_petLevel: number,
    v1_71_5_activePet: string,
    v1_71_5_unlockedPets: string,
    v1_71_5_petInDailyLogin: number,

    // Farm
    v2_9_0_farm_slots: string,
    v2_9_0_unlocked_farm_areas: string,
    v2_9_0_new_unlocked_items: string,
    v2_28_3_farm_items_rewarded: string,
    v2_33_0_farm_items_seen: string,

    // Progress ---------------------------------------------------------------
    fields: string,
    stars: string,
    friendHelperAttempts: string,
    v2_44_2_levelAttempts: string,
    maxWorld: number,
    FTUX: string,
    lastClaimedSpinLevel: number,

    // If player had spun first time the wheels
    spunFirstNormalWheel: number,
    spunFirstDiamondWheel: number,

    currencyShareCount: number,
    wheelSpinCount: number,
    optInReward: number,
    levelsSinceAd: number,
    shopGiftsCollected: number,
    adsWatched: number,
    difficultyReduction: number,
    // Daily Login
    loginStreak: number,
    v1_62_0_dailyLoginRewardsClaimed: string,
    v2_7_1_payerReactivationPurchases: string,

    // Settings ---------------------------------------------------------------
    [AudioStreamType.MUSIC]: number,
    [AudioStreamType.SFX]: number,
    cinematicMode: number,

    // Social
    receivedMessages: string,
    friendGifts: string,
    friendAltGifts: string,
    friendHelper: string,
    friendCurrencyRequests: string,
    friendsSentCurrency: string,

    // Timers
    [TimerType.LAST_PURCHASE]: string,
    [TimerType.INITIAL_LOGIN]: string,
    [TimerType.HARVEST]: string,
    [TimerType.SEND_GIFT_REQUEST_DATE]: string,
    [TimerType.GIFT]: string,
    [TimerType.LAST_LOGIN]: string,
    [TimerType.SPIN_WHEEL]: string,
    // [TimerType.GIFT_DATE]: string,
    [TimerType.LAST_LOGIN_WEEK]: string,
    [TimerType.FOX_DATE]: string,
    [TimerType.SHOP_GIFT]: string,
    [TimerType.AD_COOLDOWN]: string,
    [TimerType.DYNAMIC_HELPER_AI]: string,
    [TimerType.STARTER_PACK_OFFER_SHOWN]: string,
    [TimerType.PAYER_REACTIVATION_OFFER_START]: string,
    [TimerType.FARM_MINI_GAME]: string,

    // Quests
    v1_51_0_quests: string,
    v2_27_0_event_quests: string,

    // Other
    v1_51_0_usedCheats: number,
    isFbInstantUnified: number,

    premiumWheelResetLevel: number,
    premiumWheelSpinsAmount: number,
    gdprCompliance: boolean,
    appScopedId: string,
    deviceId: string,
    facebookUserId: string,

    missionAttemptsStartTimestamp: string,
    missionAttempts: number,
    plusFiveBoostersUsed: number,
    
    // Cross-promo game and player ID tracking.
    otherGameIds: string,
} as const;

const iPlayerDataTypeObjectDeprecated = {
    /**
     * @deprecated chest data before the star chest feature
     * Kept for reminder of save data key usage
     */
    chests: string,

    [TimerType.MISSION]: string,
    [TimerType.KEY_MISSION]: string,
    /**
     * @deprecated unused mission data
     */
    completedDailyMissions: number,
    /**
     * @deprecated unused mission data
     */
    completedWeeklyMissions: number,

    /** @deprecated */
    v1_75_0_payerReactivationOfferPurchased: number,
};

type Writeable<T> = {
    -readonly [key in keyof T]: T[key];
};

// TODO: make sure there is no collision
type IPlayerData = Writeable<typeof iPlayerDataTypeObjectMandatory> &
    Partial<Writeable<typeof iPlayerDataTypeObjectOptional>> &
    Partial<Writeable<typeof iPlayerDataTypeObjectDeprecated>>;

export default IPlayerData;

// FIXME: remove
// I don't know if this is intended behavior,
// refactored this in separate array to preserve currently tested state of the game
const fixmeOtherKeysToSkip: Array<keyof IPlayerData> = [
    TimerType.SEND_GIFT_REQUEST_DATE,
    // FIXME: it has same value as `GIFT_DATE`
    TimerType.GIFT,
    // FIXME: it has same value as `GIFT`
    TimerType.GIFT_DATE,
    TimerType.LAST_LOGIN_WEEK,
    TimerType.FOX_DATE,
];

export const playerDataObjectKeysToLoad = {
    ...iPlayerDataTypeObjectMandatory,
    ...iPlayerDataTypeObjectOptional,
    // ...iPlayerDataTypeObjectDeprecated,
};
export const playerDataKeysToLoad = (Object.keys(playerDataObjectKeysToLoad) as Array<keyof IPlayerData>).filter(
    key => !fixmeOtherKeysToSkip.includes(key)
);
